import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAdminAuth } from './contexts/AdminAuthContext';
import { useAffiliateSession } from './contexts/AffiliateSessionContext';
import { useDemoMode } from './contexts/DemoModeContext';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { InfluencerLoginPage } from './pages/InfluencerLoginPage';
import { InfluencerDashboardPage } from './pages/InfluencerDashboardPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminAffiliatesPage } from './pages/AdminAffiliatesPage';

function FullScreenMessage({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4 text-center text-sm text-[rgb(var(--text-muted))]">{children}</div>;
}

// Dois mecanismos de acesso completamente independentes (pedido explícito
// do proprietário: o influenciador nunca passa perto do Supabase Auth do
// admin) — cada rota é protegida pelo gate certo, sem nenhuma <Routes>
// aninhada (evita qualquer ambiguidade de resolução de path relativo).
function AdminGate({ children }: { children: ReactNode }) {
  const { loading, session, isAdmin, adminCheckError, signOut } = useAdminAuth();
  const { enabled: demoModeEnabled } = useDemoMode();

  if (demoModeEnabled) {
    return <>{children}</>;
  }

  if (loading) {
    return <FullScreenMessage>Carregando…</FullScreenMessage>;
  }

  if (!session) {
    return <AdminLoginPage />;
  }

  if (!isAdmin) {
    return (
      <FullScreenMessage>
        <div className="space-y-3">
          <p>{adminCheckError ?? 'Esta conta não tem acesso ao painel administrativo.'}</p>
          <button type="button" className="btn-secondary" onClick={() => signOut()}>
            Sair
          </button>
        </div>
      </FullScreenMessage>
    );
  }

  return <>{children}</>;
}

function InfluencerGate({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAffiliateSession();
  const { enabled: demoModeEnabled } = useDemoMode();
  return isLoggedIn || demoModeEnabled ? <>{children}</> : <InfluencerLoginPage />;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/admin"
        element={
          <AdminGate>
            <AdminDashboardPage />
          </AdminGate>
        }
      />
      <Route
        path="/admin/afiliados"
        element={
          <AdminGate>
            <AdminAffiliatesPage />
          </AdminGate>
        }
      />
      <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
      <Route
        path="/"
        element={
          <InfluencerGate>
            <InfluencerDashboardPage />
          </InfluencerGate>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
